import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Eye,
  Edit,
  Trash,
  MoreHorizontal,
  Calendar,
  Clock,
  ArrowUpDown,
  AlertCircle,
  Search,
  X,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useBlogPosts, PostStatus, BlogPost } from '@/hooks/useBlogPosts';
import { usePermissions } from '@/hooks/usePermissions';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

interface BlogPostListProps {
  filter?: PostStatus | 'all';
}

// Helper function to render status badge
const StatusBadge = ({ status }: { status: PostStatus }) => {
  switch (status) {
    case 'published':
      return <Badge className="bg-green-500">Published</Badge>;
    case 'draft':
      return <Badge variant="outline">Draft</Badge>;
    case 'scheduled':
      return <Badge className="bg-blue-500">Scheduled</Badge>;
    default:
      return <Badge variant="outline">Unknown</Badge>;
  }
};

// Format date for display
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const BlogPostList = ({ filter = 'all' }: BlogPostListProps) => {
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<'created_at' | 'title'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [searchDebounce, setSearchDebounce] = useState('');
  
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const canManageBlog = hasPermission('manage:blog');
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  
  // Get blog posts with filters applied - fixed to handle status filtering correctly
  const { data, isLoading, error, refetch } = useBlogPosts({
    page,
    pageSize: 10,
    search: searchDebounce,
    sortBy,
    sortOrder,
    status: filter === 'all' ? 'all' : filter,
    // Only set onlyPublished to true when specifically viewing published posts
    // This fixes the issue with the drafts filter not working
    onlyPublished: filter === 'published',
  });

  const handleSort = (field: 'created_at' | 'title') => {
    if (sortBy === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to descending for created_at, ascending for title
      setSortBy(field);
      setSortOrder(field === 'created_at' ? 'desc' : 'asc');
    }
    // Reset to first page when sort changes
    setPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    // Reset to first page when search changes
    setPage(1);
  };
  
  const clearSearch = () => {
    setSearch('');
    setPage(1);
  };

  const handleDeleteClick = (post: BlogPost) => {
    setSelectedPost(post);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPost) return;
    
    try {
      // Delete the post from the database
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', selectedPost.id);
      
      if (error) throw error;
      
      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      await queryClient.invalidateQueries({ queryKey: ['blog-stats'] });
      
      toast.success(`Post "${selectedPost.title}" deleted`);
      
      // Close the dialog
      setDeleteConfirmOpen(false);
      setSelectedPost(null);
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const handleEditPost = (post: BlogPost) => {
    navigate(`/dashboard/blog/posts/${post.id}`);
  };

  const handlePreviewPost = (post: BlogPost) => {
    // Use slug if available, otherwise use the ID route
    if (post.slug) {
      navigate(`/dashboard/blog/preview/${post.slug}`);
    } else {
      navigate(`/dashboard/blog/preview/id/${post.id}`);
    }
  };

  return (
    <div className="space-y-4">
      {/* Search input */}
      <div className="flex items-center space-x-2">
        <div className="relative flex-1 max-w-sm">
          <Search size={18} className="absolute left-2.5 top-2.5 text-gray-500" />
          <Input
            placeholder="Search posts..."
            value={search}
            onChange={handleSearchChange}
            className="pl-9 pr-9"
          />
          {search && (
            <button 
              onClick={clearSearch}
              className="absolute right-2.5 top-2.5 text-gray-500 hover:text-gray-700"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="text-center p-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          <p className="mt-2 text-sm text-slate-500">Loading posts...</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="p-8 bg-red-50 text-red-700 rounded-lg flex flex-col items-center justify-center text-center">
          <AlertCircle size={24} className="mb-2" />
          <p className="font-medium">Error loading blog posts</p>
          <p className="text-sm mt-1 text-red-600">Please try again or check your connection.</p>
          <Button variant="outline" onClick={() => refetch()} className="mt-3">
            Try Again
          </Button>
        </div>
      )}

      {/* Empty state */}
      {data && data.data.length === 0 && !isLoading && (
        <div className="text-center p-8 bg-slate-50 rounded-lg">
          {search ? (
            <>
              <p className="text-slate-600 mb-2">No blog posts found matching "{search}"</p>
              <Button variant="outline" onClick={clearSearch} className="mt-2">
                Clear Search
              </Button>
            </>
          ) : filter !== 'all' ? (
            <>
              <p className="text-slate-600 mb-2">No {filter} posts found</p>
              {canManageBlog && (
                <Button onClick={() => navigate('/dashboard/blog/posts/new')} className="mt-2">
                  Create New Post
                </Button>
              )}
            </>
          ) : (
            <>
              <p className="text-slate-600 mb-2">No blog posts found</p>
              {canManageBlog && (
                <Button onClick={() => navigate('/dashboard/blog/posts/new')} className="mt-2">
                  Create New Post
                </Button>
              )}
            </>
          )}
        </div>
      )}

      {/* Posts table */}
      {data && data.data.length > 0 && (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50%]">
                  <Button
                    variant="ghost"
                    onClick={() => handleSort('title')}
                    className="flex items-center space-x-1 px-0"
                  >
                    <span>Title</span>
                    {sortBy === 'title' && (
                      <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                    {sortBy !== 'title' && <ArrowUpDown size={16} />}
                  </Button>
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    onClick={() => handleSort('created_at')}
                    className="flex items-center space-x-1 px-0"
                  >
                    <span>Date</span>
                    {sortBy === 'created_at' && (
                      <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                    {sortBy !== 'created_at' && <ArrowUpDown size={16} />}
                  </Button>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.data.map((post) => (
                <TableRow key={post.id}>
                  <TableCell className="font-medium">
                    <div className="truncate max-w-[350px]" title={post.title}>
                      {post.title}
                    </div>
                    {post.excerpt && (
                      <div className="text-xs text-slate-500 truncate max-w-[350px]" title={post.excerpt}>
                        {post.excerpt}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={post.status} />
                    {post.status === 'scheduled' && post.scheduled_for && (
                      <div className="flex items-center text-xs text-slate-500 mt-1">
                        <Calendar size={12} className="mr-1" />
                        {formatDate(post.scheduled_for)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center text-sm text-slate-500">
                      <Calendar size={14} className="mr-1" />
                      {formatDate(post.created_at)}
                    </div>
                    <div className="flex items-center text-xs text-slate-400 mt-1">
                      <Clock size={12} className="mr-1" />
                      {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end space-x-2">
                      {/* Preview Button */}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handlePreviewPost(post)}
                        title="Preview Post"
                      >
                        <Eye size={16} />
                      </Button>
                      
                      {/* Edit Button (visible if user can manage blog) */}
                      {canManageBlog && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditPost(post)}
                          title="Edit Post"
                        >
                          <Edit size={16} />
                        </Button>
                      )}
                      
                      {/* Actions Dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handlePreviewPost(post)}
                            className="flex items-center"
                          >
                            <Eye size={14} className="mr-2" />
                            Preview
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleEditPost(post)}
                            disabled={!canManageBlog}
                            className="flex items-center"
                          >
                            <Edit size={14} className="mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteClick(post)}
                            disabled={!canManageBlog}
                            className="flex items-center text-red-600 focus:text-red-600"
                          >
                            <Trash size={14} className="mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {data && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between space-x-2 py-4">
          <div className="text-sm text-slate-500">
            Page {page} of {data.pagination.totalPages}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => setPage(page + 1)}
              disabled={page >= data.pagination.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the post{' '}
              <span className="font-semibold">"{selectedPost?.title}"</span>? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogPostList;
